import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import TextView from "../../../components/TextView";
import Button from "../../../components/Button";
import { colors } from "../../../utils";
import { getLoggedInUser } from "../../../utils/helpers";
import { getUserWheels } from "../../../services/api";
import { attachedWheelsList, headerCheckboxState } from "../Capture.helpers";
import {
  normalizeWheels,
  applySearchAndSort,
  SORT_OPTIONS,
} from "./captureWheelPicker";
import PickerWheelThumb from "./PickerWheelThumb";
import {
  ModalBody,
  WheelList,
  WheelRow,
  ModalActions,
  ActionBtnWrap,
  StateWrap,
  AttachedChip,
  SearchWrap,
  SearchInput,
  AttachSortSelect,
  AttachPickerToolbar,
} from "../styles";

const StyledDialog = styled(Dialog)(() => ({
  ".MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
  ".MuiPaper-root": {
    padding: "8px 8px 16px",
    border: "1px solid #E1E1E1",
    borderRadius: 12,
    width: 560,
    maxWidth: "92vw",
    maxHeight: "calc(100% - 64px)",
    display: "flex",
    flexDirection: "column",
  },
  // Same contract as Choose-a-Wheel: the paper stays put and ONLY the
  // wheel list scrolls. Default MuiDialogContent overflow:auto was clipping
  // extra rows instead of handing them to WheelList's 320px pane.
  ".MuiDialogContent-root": {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
}));

const ScrollingWheelList = styled(WheelList)({
  flex: "1 1 320px",
  minHeight: 0,
  height: 320,
  maxHeight: 320,
  overflowY: "auto",
  overscrollBehavior: "contain",
});

const SelectAllRow = styled("button")({
  display: "flex",
  alignItems: "center",
  gap: 12,
  width: "100%",
  textAlign: "left",
  border: "none",
  background: "transparent",
  padding: "4px 4px 8px",
  cursor: "pointer",
  fontFamily: '"DM Sans", sans-serif',
});

const CheckMark = styled("span", {
  shouldForwardProp: (prop) => prop !== "checked" && prop !== "mixed",
})(({ checked, mixed }) => ({
  width: 22,
  height: 22,
  flexShrink: 0,
  boxSizing: "border-box",
  border: `2px solid ${
    checked || mixed ? colors.PrimaryPurple500 : colors.darkGrey
  }`,
  backgroundColor: checked || mixed ? colors.PrimaryPurple500 : colors.white,
  borderRadius: 5,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: colors.white,
}));

const wheelKey = (wheel) => String(wheel.id);

/**
 * Inbox Detach when a capture is on MORE than one wheel.
 *
 * Same picker chrome as "Choose a Wheel" (search, sort, preview rows, scroll),
 * with checkboxes + Select all so several links can be dropped at once via
 * POST /capture/items/{id}/detach-wheels/  { wheel_ids: [...] }.
 */
const DetachWheelModal = ({ open, item, onClose, onPick, initialSelectAll = false }) => {
  const userId = getLoggedInUser()?.id;
  const linked = useMemo(() => attachedWheelsList(item), [item]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");

  useEffect(() => {
    if (open) {
      setSelected(initialSelectAll ? linked.map((wheel) => wheel.id) : []);
      setSearch("");
      setSortBy("name");
    }
  }, [open, item?.id, initialSelectAll, linked]);

  const { data, isLoading, isError, refetch } = useQuery(
    ["captureAttachWheels", userId],
    () => getUserWheels(userId),
    { enabled: open && !!userId, refetchOnWindowFocus: false }
  );

  const catalog = useMemo(() => normalizeWheels(data), [data]);
  const wheels = useMemo(() => {
    const byId = new Map(catalog.map((wheel) => [String(wheel.id), wheel]));
    return linked.map((wheel) => {
      const rich = byId.get(String(wheel.id));
      return {
        id: wheel.id,
        name: rich?.name || wheel.name || "Untitled wheel",
        image: rich?.image || null,
        portrait: rich?.portrait || null,
        chartData: rich?.chartData || [],
        meta: rich?.meta || "",
        createdAt: rich?.createdAt || null,
      };
    });
  }, [linked, catalog]);

  const visibleWheels = useMemo(
    () => applySearchAndSort(wheels, search, sortBy),
    [wheels, search, sortBy]
  );

  const selectedSet = new Set(selected.map(String));
  const visibleSelectedCount = visibleWheels.filter((wheel) =>
    selectedSet.has(wheelKey(wheel))
  ).length;
  const selectAllState = headerCheckboxState(
    visibleSelectedCount,
    visibleWheels.length
  );
  const chosen = wheels.filter((wheel) => selectedSet.has(wheelKey(wheel)));

  const toggle = (id) => {
    const key = String(id);
    setSelected((prev) =>
      prev.map(String).includes(key)
        ? prev.filter((value) => String(value) !== key)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = visibleWheels.map((wheel) => wheel.id);
    if (selectAllState === "all") {
      const drop = new Set(visibleIds.map(String));
      setSelected((prev) => prev.filter((id) => !drop.has(String(id))));
    } else {
      setSelected((prev) => {
        const next = new Set(prev.map(String));
        visibleIds.forEach((id) => next.add(String(id)));
        return wheels
          .filter((wheel) => next.has(wheelKey(wheel)))
          .map((wheel) => wheel.id);
      });
    }
  };

  const pickChosen = () => {
    if (chosen.length === 0) return;
    onPick(chosen.map((wheel) => ({ id: wheel.id, name: wheel.name })));
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle>
        <TextView variant="title">Detach from which Wheels?</TextView>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 14, color: colors.darkGrey }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ModalBody style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <TextView variant="text" color={colors.textGrey}>
            This capture stays in your inbox. Only the wheels you select are unlinked.
          </TextView>
          {isLoading ? (
            <StateWrap style={{ minHeight: 200 }}>
              <CircularProgress
                size={28}
                style={{ color: colors.PrimaryPurple500 }}
              />
              <TextView variant="largeText" color={colors.textGrey}>
                Loading your wheels…
              </TextView>
            </StateWrap>
          ) : isError ? (
            <StateWrap style={{ minHeight: 200 }}>
              <TextView variant="largeText" color={colors.error}>
                Couldn&apos;t load wheels.
              </TextView>
              <ActionBtnWrap>
                <Button secondary onClick={() => refetch()}>
                  Retry
                </Button>
              </ActionBtnWrap>
            </StateWrap>
          ) : (
            <>
              {wheels.length > 0 && (
                <AttachPickerToolbar>
                  <SearchWrap>
                    <SearchRoundedIcon fontSize="small" />
                    <SearchInput
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search wheels"
                      aria-label="Search wheels"
                    />
                  </SearchWrap>
                  <AttachSortSelect
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Sort wheels"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </AttachSortSelect>
                </AttachPickerToolbar>
              )}
              {visibleWheels.length > 0 && (
                <SelectAllRow
                  type="button"
                  role="checkbox"
                  aria-checked={
                    selectAllState === "all"
                      ? "true"
                      : selectAllState === "some"
                      ? "mixed"
                      : "false"
                  }
                  aria-label="Select all"
                  onClick={toggleSelectAll}
                >
                  <CheckMark
                    checked={selectAllState === "all"}
                    mixed={selectAllState === "some"}
                  >
                    {selectAllState === "all" && (
                      <CheckRoundedIcon style={{ fontSize: 15 }} />
                    )}
                    {selectAllState === "some" && (
                      <RemoveRoundedIcon style={{ fontSize: 15 }} />
                    )}
                  </CheckMark>
                  <TextView variant="largeText" color={colors.black}>
                    Select all
                  </TextView>
                </SelectAllRow>
              )}
              <ScrollingWheelList>
                {wheels.length === 0 ? (
                  <StateWrap style={{ minHeight: 160 }}>
                    <TextView variant="largeText" color={colors.textGrey}>
                      This capture isn&apos;t on any wheels.
                    </TextView>
                  </StateWrap>
                ) : visibleWheels.length === 0 ? (
                  <StateWrap style={{ minHeight: 160 }}>
                    <TextView variant="largeText" color={colors.textGrey}>
                      No wheels match &quot;{search}&quot;.
                    </TextView>
                  </StateWrap>
                ) : (
                  visibleWheels.map((wheel) => {
                    const checked = selectedSet.has(wheelKey(wheel));
                    return (
                      <WheelRow
                        key={wheel.id}
                        type="button"
                        role="checkbox"
                        aria-checked={checked}
                        aria-label={wheel.name || "Attached"}
                        active={checked || undefined}
                        onClick={() => toggle(wheel.id)}
                      >
                        <CheckMark checked={checked}>
                          {checked && <CheckRoundedIcon style={{ fontSize: 15 }} />}
                        </CheckMark>
                        <PickerWheelThumb wheel={wheel} />
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <TextView
                            variant="largeText"
                            color={colors.black}
                            style={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {wheel.name}
                          </TextView>
                          {wheel.meta ? (
                            <TextView variant="text" color={colors.textGrey}>
                              {wheel.meta}
                            </TextView>
                          ) : null}
                        </div>
                        <AttachedChip style={{ textTransform: "none" }}>
                          Media Already Added
                        </AttachedChip>
                      </WheelRow>
                    );
                  })
                )}
              </ScrollingWheelList>
            </>
          )}
          <ModalActions>
            <ActionBtnWrap>
              <Button secondary onClick={onClose}>
                Cancel
              </Button>
            </ActionBtnWrap>
            <ActionBtnWrap>
              <Button onClick={pickChosen} disabled={chosen.length === 0}>
                Detach
              </Button>
            </ActionBtnWrap>
          </ModalActions>
        </ModalBody>
      </DialogContent>
    </StyledDialog>
  );
};

export default DetachWheelModal;
